/**
 * 검색어·토픽·감성 키워드 하이라이트.
 *
 * ⚠️ **이 파일은 태그를 «넣는» 쪽이고 걷지 않는다.** 이름(`…WithHtmlRemoval`)이 걷는 것처럼
 * 들리지만 안 걷는다.
 *
 * 🔑 **그래서 이 파일의 출력은 «아직 안전하지 않다».** 넣은 태그와 «원문에 섞여 온» 태그가
 * 같이 들어 있다. 안전해지는 것은 `components/cmui/post-card/SafeHtml` 이 DOMPurify 로
 * 씻은 뒤다 — **세척이 마지막 변환**이다.
 */

/**
 * 텍스트에 키워드 하이라이트를 적용 (sentiment evidence 포함)
 * 전처리 단계에서 이미 HTML 태그가 제거된 텍스트를 받습니다.
 * @param {string} text - 원본 텍스트 (전처리로 HTML 태그가 이미 제거된 상태)
 * @param {string} searchKeyword - 검색 키워드
 * @param {Array<string>} topicKeywords - 토픽 키워드 배열
 * @param {string} clientSearch - 클라이언트 검색어
 * @param {Array<string>} sentimentPositive - 긍정 sentiment 키워드 배열
 * @param {Array<string>} sentimentNegative - 부정 sentiment 키워드 배열
 * @returns {string} 하이라이트가 적용된 텍스트
 */
/**
 * HTML 태그(<...>) 바깥의 텍스트에서만 키워드 치환 (태그 내부는 건드리지 않음 → placeholder 불필요)
 * @param {string} inputText - 전체 문자열 (이미 하이라이트 태그가 섞여 있을 수 있음)
 * @param {string} keyword - 검색할 키워드
 * @param {string} replacement - 치환 문자열 ($1 = 매칭된 키워드)
 */
/** 단독 낱말로만 칠할 짧은 라틴 말 — 앞머리 `@`·`#` 는 허용 */
const SHORT_LATIN_WORD = /^[@#]*[0-9A-Za-z]{1,3}$/;

function replaceInTextOnly(inputText: string, keyword: string, replacement: string): string {
  if (!keyword) return inputText;
  const escapeRegExp = (str: string) => String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const raw = String(keyword || '').trim();
  if (!raw) return inputText;

  // 공백/nbsp/줄바꿈 변형에도 하이라이트가 되도록
  // "fine lines and wrinkles" → /fine[\s\u00A0]+lines[\s\u00A0]+and[\s\u00A0]+wrinkles/i
  const rawParts = raw.split(/\s+/).filter(Boolean);
  // 키워드 생성 과정에서 토큰이 중복되는 경우가 있어(예: "medicube ... medicube")
  // 하이라이트는 실제 본문 매칭을 우선하므로, 순서를 유지한 채 중복 토큰은 제거합니다.
  const uniqueRawParts = rawParts.filter((part, idx) => rawParts.indexOf(part) === idx);
  const keywordParts = uniqueRawParts.map((part) => {
    const p = String(part || '').trim();
    if (!p) return '';
    const normalized = p.replace(/^[@#]+/, '');
    if (!normalized) return '';
    // 본문에서는 각 토큰이 "#keyword" / "@keyword" 형태로 붙는 경우가 많아 접두를 선택 허용
    return `(?:[@#])?${escapeRegExp(normalized)}`;
  }).filter(Boolean);

  // 단어 사이 구분자는 공백/nbsp/"_" 뿐 아니라 "!" 같은 특수문자도 자주 등장
  // 예: "medicube!zero" / "fine!lines" / "메디큐브!제로모공"
  // (영문/숫자/한글) "단어문자"가 아닌 구분자들을 간격으로 허용한다
  const gap = '[^0-9A-Za-z가-힣<>]+';
  const replaceByRegex = (text: string, regex: RegExp) => {
    // 태그를 구분자로 split 하되 구분자도 유지: [텍스트, 태그, 텍스트, ...]
    const textParts = text.split(/(<[^>]+>)/g);
    for (let i = 0; i < textParts.length; i += 1) {
      if (textParts[i].startsWith('<') && textParts[i].endsWith('>')) continue;
      textParts[i] = textParts[i].replace(regex, replacement);
    }
    return textParts.join('');
  };

  // 1) 우선: 연속 구절(토큰이 순서대로 붙는 경우)
  {
    const joined = keywordParts.length <= 1 ? keywordParts[0] : keywordParts.join(gap);
    // 🔑 3자 이하 라틴·숫자 한 낱말(`vt` · `dr`)은 단독 낱말일 때만 — 「Hydration」의 dr · 「VTR」을 칠하지 않는다
    const pattern = SHORT_LATIN_WORD.test(raw) ? `(?<![0-9A-Za-z])${joined}(?![0-9A-Za-z])` : joined;
    const regex = new RegExp(`(${pattern})`, 'gi');
    const replaced = replaceByRegex(inputText, regex);
    if (replaced !== inputText) return replaced;
  }

  // 구절 매칭에 실패하면(토큰 단위 폴백 없이) 원문을 그대로 반환합니다.
  return inputText;
}

export const highlightTextWithHtmlRemoval = (
  text: string | null | undefined,
  searchKeyword: string = '',
  topicKeywords: string[] = [],
  clientSearch: string = '',
  sentimentPositive: string[] = [],
  sentimentNegative: string[] = [],
): string | null | undefined => {
  if (!text) return text;

  // 전처리 단계에서 이미 HTML 태그가 제거되었으므로 그대로 사용
  let highlightedText = String(text || '');

  const searchBg = '#FEE496'; // 검색 키워드: 노란색 (최고 우선)
  const clientBg = '#cdf6e4'; // 클라이언트 검색어: 초록 (중간 우선)
  const topicBg = '#FEE496';  // 토픽 키워드: 노란색 (검색과 동일)
  const positiveColor = '#3b82f6'; // 긍정 sentiment: 파란색 글자
  const negativeColor = '#ef4444'; // 부정 sentiment: 빨간색 글자

  // 태그 바깥 텍스트에서만 치환 (placeholder 없이 안전하게 처리)
  const replaceWithoutTags = (inputText: string, keyword: string, replacement: string) =>
    replaceInTextOnly(inputText, keyword, replacement);

  // 1) 토픽 키워드 (가장 낮은 우선순위)
  (Array.isArray(topicKeywords) ? topicKeywords : []).forEach((keyword) => {
    const k = String(keyword || '').trim();
    if (!k) return;
    highlightedText = replaceWithoutTags(
      highlightedText,
      k,
      `<mark style="background-color: ${topicBg}; color: #000;">$1</mark>`
    );
  });

  // 2) 부정 sentiment 키워드 - 글자색만 변경
  (Array.isArray(sentimentNegative) ? sentimentNegative : []).forEach((keyword) => {
    const k = String(keyword || '').trim();
    if (!k) return;
    highlightedText = replaceWithoutTags(
      highlightedText,
      k,
      `<span style="color: ${negativeColor};">$1</span>`
    );
  });

  // 3) 긍정 sentiment 키워드 - 글자색만 변경
  (Array.isArray(sentimentPositive) ? sentimentPositive : []).forEach((keyword) => {
    const k = String(keyword || '').trim();
    if (!k) return;
    highlightedText = replaceWithoutTags(
      highlightedText,
      k,
      `<span style="color: ${positiveColor};">$1</span>`
    );
  });

  // 4) 클라이언트 검색어 (중간 우선순위). 쉼표로 여러 개 들어온 경우 각각 처리
  {
    const raw = String(clientSearch || '').trim();
    if (raw) {
      const tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
      const list = tokens.length > 0 ? tokens : [raw];
      list.forEach((t) => {
        highlightedText = replaceWithoutTags(
          highlightedText,
          t,
          `<mark style="background-color: ${clientBg}; color: #000;">$1</mark>`
        );
      });
    }
  }

  // 5) 검색 키워드 (가장 높은 우선순위). 쉼표로 여러 개 들어온 경우 각각 처리
  {
    const raw = String(searchKeyword || '').trim();
    if (raw) {
      const tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
      const list = tokens.length > 0 ? tokens : [raw];
      list.forEach((t) => {
        highlightedText = replaceWithoutTags(
          highlightedText,
          t,
          `<mark style="background-color: ${searchBg}; color: #000;">$1</mark>`
        );
      });
    }
  }

  return highlightedText;
};
